import React from 'react';
import Cards from '@ifit/mp-cards';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { default as Link } from '../../components/link-with-disabled';
import { isLocked } from '@ifit/equipment-compatibility';
import { isMobile } from '../../helpers/responsive';
import { WeakType } from '../../typescript-migration';
import { IMembershipType } from '@ifit/gateway-wolf-athenaeum-service';
import {
  AnalyticsWrapper,
  IAnalyticsWrapperProps,
  IPositiveEngagement,
} from '../../helpers/analytics';
import { useEquipmentType } from '../../client/query/equipment-type';
import { useUnlockedWorkout } from '../../client/query/unlocked-workout';
import { useConfig } from '../../store/reducers/config';
import { useDeviceType } from '../../store/device-type';
import { useMembership } from '../../client/query/users';
import { useCompletionsQuery } from '../../client/query/completions';
import { useEstimatesAndRating } from '../../client/query/estimate-and-rate';

type ContentItemType = 'workout' | 'program' | 'trainer' | 'challenge';

export interface IContentItem {
  type: ContentItemType;
  item: WeakType;
}

export interface IContentItemAnalytics {
  event?: string;
  engagementData: Partial<IPositiveEngagement>;
}

interface IProps {
  item: IContentItem;
  large?: boolean;
  showCategory: boolean;
  showSubcategory: boolean;
  disableAvatar?: boolean;
  analyticsData?: IContentItemAnalytics;
  themeClass?: string;
}

interface IConditionalLinkProps {
  shouldWrap: boolean;
  wrapper: WeakType;
  parentProps: Pick<IProps, 'item' | 'disableAvatar'>;
}
const ConditionalLink: React.FC<IConditionalLinkProps> = ({
  shouldWrap,
  wrapper,
  children,
}) => (shouldWrap ? wrapper(children) : children);

const ConditionalAnalytics: React.FC<{
  analyticsProps: IAnalyticsWrapperProps;
}> = ({ analyticsProps, children }) =>
  analyticsProps ? (
    <AnalyticsWrapper {...analyticsProps}>{children}</AnalyticsWrapper>
  ) : (
    <>{children}</>
  );

interface ITrainerLinkProps extends Pick<IProps, 'item' | 'disableAvatar'> {}
const getTrainerId = (contentItem: IContentItem): string => {
  if (!contentItem) return;
  const { item, type } = contentItem;
  switch (type) {
    case 'trainer':
      return item.itemId;
    case 'program':
      return get(item, 'trainersWithImages[0].id');
    default:
      return item.trainerId;
  }
};

const createTitle = (item: WeakType, type: string): string => {
  const title = type === 'trainer' ? item.name : item.title;
  return (title || '').replace(/_/g, ' ');
};

const TrainerLink: React.FC<ITrainerLinkProps> = ({
  item,
  disableAvatar,
  children,
}) => (
  <Link
    data-testid="Link"
    stopPropagation
    disabled={disableAvatar}
    to={`/trainer/${getTrainerId(item)}`}
  >
    {children}
  </Link>
);

const PureContentItem: React.FC<IProps> = ({
  item: { type, item },
  showCategory,
  showSubcategory,
  large = false,
  disableAvatar = false,
  analyticsData,
  themeClass,
}) => {
  const deviceType = useDeviceType();
  const { equipmentType } = useEquipmentType();
  const { useMetricUnits, isLoggedIn, isClub, isEmbedded } = useConfig();
  const { membershipType, isPaused } = useMembership();
  const dashboardUnlockedWorkoutId = useUnlockedWorkout();
  const { data: completions } = useCompletionsQuery();
  const { data: { estimates, ratings } = {} } = useEstimatesAndRating(
    item.itemId,
    type === 'workout' && (!item.estimates || !item.rating),
  );
  const categoryInfoKey =
    showSubcategory ||
    item.category === 'International' ||
    item.category === 'ActivePulse Training'
      ? 'subcategory'
      : showCategory && 'category';
  const categoryText = (categoryInfoKey && item[categoryInfoKey]) || undefined;
  const data = {
    ...item,
    ...(type === 'workout'
      ? {
          estimates: estimates || item.estimates,
          rating: ratings || item.rating,
        }
      : {}),
    title: createTitle(item, type),
    lastCompleted:
      completions && completions[item.itemId]
        ? completions[item.itemId]
        : undefined,
  };

  const accessParams = {
    user: {
      premium: membershipType === IMembershipType.Premium,
      isPaused,
      isLoggedIn,
    },
    console: { isEmbedded, isClub, workoutFilters: [] },
    dashboardUnlockedWorkoutId: [dashboardUnlockedWorkoutId],
    workout: {
      id: data?.itemId,
      subscriptionTypes: data?.subscriptionTypes,
      workoutFilters: [],
    },
  };

  const programItemInfo = data?.programInfo;
  const locked = isLocked(accessParams);

  const isChallenge =
    Boolean(data?.challengeEndTimestamp) || type === 'challenge';

  const cardRendered = {
    program: 'series',
    trainer: 'trainer',
    challenge: 'series',
  }[type];

  let cardSize;

  if (isMobile(deviceType)) {
    cardSize = 'small';
  } else if (!isMobile(deviceType) && large) {
    cardSize = 'large';
  }

  const completeAnalyticsProps: IAnalyticsWrapperProps = analyticsData
    ? {
        startPositiveEngagementData: {
          event: analyticsData?.event ?? 'onClickCapture',
          engagementData: {
            entity: type,
            id: item.itemId,
            ...analyticsData?.engagementData,
          },
        },
      }
    : null;
  return (
    <ConditionalAnalytics analyticsProps={completeAnalyticsProps}>
      <ConditionalLink
        shouldWrap={type === 'trainer'}
        /* eslint-disable-next-line react/jsx-no-bind */
        wrapper={children => (
          <TrainerLink item={{ item, type }} disableAvatar={disableAvatar}>
            {children}
          </TrainerLink>
        )}
        parentProps={{ item, disableAvatar }}
      >
        <Cards
          category={categoryText}
          programInfo={programItemInfo}
          data={data}
          disableNative={type === 'trainer'}
          cardType={cardRendered}
          cardSize={cardSize}
          locked={locked}
          useMetricUnits={useMetricUnits}
          equipmentType={equipmentType}
          themeClass={themeClass}
          isChallenge={isChallenge}
        />
      </ConditionalLink>
    </ConditionalAnalytics>
  );
};

export default PureContentItem;

/**
 * @deprecated this type should not be used for new code
 */
export const contentItemPropType = PropTypes.oneOfType([
  PropTypes.shape({
    type: PropTypes.oneOf(['program']),
    item: PropTypes.object,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf(['workout']),
    item: PropTypes.object,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf(['trainer']),
    item: PropTypes.object,
  }),
  PropTypes.shape({
    type: PropTypes.oneOf(['section']),
    item: PropTypes.object,
  }),
]);
